import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Tooltip,
  LinearProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/stores/notificationStore';
import {
  useBookingWorkPaths,
  useUpdateWorkPathEntry,
  useWorkPathPhotoUploadUrl,
  type WorkPathTask,
  type WorkPathVerification,
} from '@/hooks/useWorkPaths';
import { describeApiError } from '@/lib/describeApiError';

interface Props {
  workPathId: string;
  bookingId: string;
  task: WorkPathTask | null;
  onClose: () => void;
}

/**
 * Mechanic-facing dialog for a single work path task.
 *
 * Lists the task's verification entries and lets the mechanic complete each
 * one (checkbox / text / numeric / photo). Writes go through the single-entry
 * update endpoint; photos reuse the shared upload-url mechanism (the resulting
 * file URL is stored as the entry value).
 *
 * The booking work path response does not echo back entry values, so this
 * dialog keeps a local map of values saved during the session to drive
 * immediate per-entry feedback and the in-dialog progress bar.
 */
export function WorkPathTaskDialog({ workPathId, bookingId, task, onClose }: Props) {
  const { t } = useTranslation('workpaths');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const updateEntry = useUpdateWorkPathEntry();
  const getUploadUrl = useWorkPathPhotoUploadUrl();

  const [uploadingEntryId, setUploadingEntryId] = useState<string | null>(null);

  // Read the LIVE task from the by-booking query (not the snapshot `task` prop),
  // so persisted entry values hydrate on open and refresh after each save's
  // refetch — no session-local fallback (#826).
  const { data: workPaths } = useBookingWorkPaths(bookingId);
  const liveTask = useMemo<WorkPathTask | null>(() => {
    if (!task) return null;
    const found = workPaths?.flatMap((wp) => wp.tasks).find((t) => t.id === task.id);
    return found ?? task;
  }, [workPaths, task]);

  const verifications = useMemo<WorkPathVerification[]>(
    () => (liveTask ? [...liveTask.verifications].sort((a, b) => a.sortOrder - b.sortOrder) : []),
    [liveTask],
  );

  const completedCount = useMemo(
    () =>
      verifications.filter((v) => {
        const value = v.value;
        return value != null && value !== '' && value !== 'false';
      }).length,
    [verifications],
  );

  const totalCount = verifications.length;
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const allComplete = totalCount > 0 && completedCount === totalCount;

  const saveEntry = useCallback(
    (entryId: string, value: string | null) => {
      updateEntry.mutate(
        { id: workPathId, bookingId, entryId, value },
        { onError: (error) => notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.saveEntryFailed') })) },
      );
    },
    [updateEntry, workPathId, bookingId, notify, tc],
  );

  const handleAddPhoto = useCallback(
    async (entryId: string, file: File) => {
      setUploadingEntryId(entryId);
      try {
        const ext = file.name.split('.').pop() ?? 'jpg';
        const { uploadUrl, fileUrl } = await getUploadUrl.mutateAsync({
          workPathId,
          entryId,
          mimeType: file.type || 'image/jpeg',
          fileExtension: ext,
        });

        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'image/jpeg' },
        });

        saveEntry(entryId, fileUrl);
        notify.success(t('task.photoUploaded'));
      } catch (error) {
        notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.photoUploadFailed') }));
      } finally {
        setUploadingEntryId(null);
      }
    },
    [getUploadUrl, workPathId, saveEntry, notify, t, tc],
  );

  if (!task) return null;

  return (
    <ResponsiveDialog
      open={!!task}
      onClose={onClose}
      mobileTitle={task.name}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: { xs: 'none', sm: 'block' } }}>{task.name}</DialogTitle>

      <DialogContent dividers data-testid="work-path-task-dialog">
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={allComplete ? 'success' : 'primary'}
            sx={{ flex: 1, borderRadius: 1, height: 6 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            whiteSpace="nowrap"
            data-testid="work-path-task-dialog-progress"
          >
            {completedCount}/{totalCount}
          </Typography>
        </Box>

        <Stack spacing={2}>
          {verifications.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('task.noVerifications')}
            </Typography>
          )}
          {verifications.map((verification) => (
            <VerificationEntryRow
              key={verification.id}
              verification={verification}
              savedValue={verification.value ?? null}
              disabled={updateEntry.isPending}
              isUploadingPhoto={uploadingEntryId === verification.id}
              onSave={(value) => saveEntry(verification.id, value)}
              onAddPhoto={(file) => handleAddPhoto(verification.id, file)}
            />
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{tc('close')}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

interface EntryRowProps {
  verification: WorkPathVerification;
  savedValue: string | null | undefined;
  disabled: boolean;
  isUploadingPhoto: boolean;
  onSave: (value: string | null) => void;
  onAddPhoto: (file: File) => Promise<void>;
}

/** The condition scale, worst last so the buttons read as a severity ramp. */
const CONDITION_OPTIONS = [
  { value: 'good', labelKey: 'condition.good', color: 'success' },
  { value: 'fair', labelKey: 'condition.fair', color: 'info' },
  { value: 'poor', labelKey: 'condition.poor', color: 'warning' },
  { value: 'critical', labelKey: 'condition.critical', color: 'error' },
] as const;

/**
 * Renders a single verification entry. Supports the five entry types
 * (checkbox, text, numeric, photo, condition) with deliberately minimal
 * interactions: a check, a quick note, a quick number, a quick photo, or one
 * tap on a rating.
 */
function VerificationEntryRow({
  verification,
  savedValue,
  disabled,
  isUploadingPhoto,
  onSave,
  onAddPhoto,
}: EntryRowProps) {
  const { t } = useTranslation('workpaths');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<string>(savedValue ?? '');

  if (verification.type === 'checkbox') {
    const checked = savedValue === 'true';
    return (
      <FormControlLabel
        data-testid="work-path-entry-checkbox"
        control={
          <Checkbox
            checked={checked}
            disabled={disabled}
            onChange={(e) => onSave(e.target.checked ? 'true' : 'false')}
            inputProps={{ 'aria-label': verification.label }}
          />
        }
        label={verification.label}
      />
    );
  }

  if (verification.type === 'photo') {
    const hasPhoto = savedValue != null && savedValue !== '';
    return (
      <Box data-testid="work-path-entry-photo">
        <Typography variant="body2" gutterBottom>
          {verification.label}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onAddPhoto(file);
              e.target.value = '';
            }}
          />
          <Tooltip title={t('task.addPhoto')}>
            <span>
              <IconButton
                color="primary"
                disabled={disabled || isUploadingPhoto}
                onClick={() => fileInputRef.current?.click()}
                aria-label={t('task.addPhoto')}
              >
                {isUploadingPhoto ? <CircularProgress size={20} /> : <PhotoCameraIcon />}
              </IconButton>
            </span>
          </Tooltip>
          {hasPhoto && (
            <Link href={savedValue} target="_blank" rel="noopener" variant="body2">
              {t('task.viewPhoto')}
            </Link>
          )}
        </Box>
      </Box>
    );
  }

  if (verification.type === 'condition') {
    return (
      <Box data-testid="work-path-entry-condition">
        <Typography variant="body2" gutterBottom>
          {verification.label}
        </Typography>
        {/* Four taps, not a dropdown: this is filled on a phone next to the
            car, and the rating is the whole interaction. */}
        <Box display="flex" flexWrap="wrap" gap={1}>
          {CONDITION_OPTIONS.map((option) => {
            const selected = savedValue === option.value;
            return (
              <Chip
                key={option.value}
                label={t(option.labelKey)}
                color={selected ? option.color : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                size="small"
                disabled={disabled}
                // Re-tapping the current rating clears it, so a mistap is
                // undoable without a separate control.
                onClick={() => { onSave(selected ? null : option.value); }}
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  // text or numeric
  const isNumeric = verification.type === 'numeric';
  return (
    <TextField
      data-testid={isNumeric ? 'work-path-entry-numeric' : 'work-path-entry-text'}
      label={verification.label}
      value={draft}
      type={isNumeric ? 'number' : 'text'}
      disabled={disabled}
      size="small"
      fullWidth
      helperText={verification.unit ? t('task.unitHint', { unit: verification.unit }) : undefined}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const trimmed = draft.trim();
        onSave(trimmed === '' ? null : draft);
      }}
    />
  );
}
